'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from './auth-context';
import { useUserSession } from './user-session-context';
import { Alert, AlertDescription, AlertTitle } from '@/platform/ui/alert';
import { Button } from '@/platform/ui/button';
import { ShieldAlert } from 'lucide-react';

type ProductKey = 'productA' | 'productB';

interface RequireProductAccessProps {
  productKey: ProductKey;
  children: React.ReactNode;
}

export function RequireProductAccess({ productKey, children }: RequireProductAccessProps) {
  const { user, loading: authLoading } = useAuth();
  const { entitlements, isSuperAdmin, loading: sessionLoading } = useUserSession();
  const router = useRouter();

  const sessionSettling = authLoading || (sessionLoading && !user);
  const hasAccess = user
    ? isSuperAdmin || entitlements.includes(productKey)
    : null;

  useEffect(() => {
    if (authLoading || sessionLoading) return;
    if (!user) {
      router.push('/auth/login');
    }
  }, [user, authLoading, sessionLoading, router]);

  if (sessionSettling || !hasAccess) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Checking access...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
